/**
 * RRMonitor RRM page (standalone repo version)
 *
 * This component runs Test GCS comparisons (folder-to-folder), renders summary cards,
 * detailed drilldowns, and supports Excel export.
 */

import React, { useEffect, useState } from 'react';
import { ref, listAll } from 'firebase/storage';
import { httpsCallable, getFunctions } from 'firebase/functions';
import * as XLSX from 'xlsx';

import { storage, app } from '../firebase';
import { useAuth } from '../AuthContext';
import Shell from '../components/Shell';
import mdrmRefUrl from '../ref/MDRMref.csv';

import './RRM.css';

const RRM = () => {
  const [error, setError] = useState(null);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [selectedView, setSelectedView] = useState(null);
  const [origFolder, setOrigFolder] = useState('');
  const [newFolder, setNewFolder] = useState('');
  const [availableFolders, setAvailableFolders] = useState([]);
  const [testGCSLoading, setTestGCSLoading] = useState(false);
  const [testGCSResult, setTestGCSResult] = useState(null);
  const [sortOrder, setSortOrder] = useState('latest'); // 'latest' or 'oldest'
  const [reportFilter, setReportFilter] = useState('all'); // 'all' | 'FR Y-9C' | 'FR Y-9LP' | 'FR Y-9SP' | 'unknown'
  const [mdrmTitleByCode, setMdrmTitleByCode] = useState({});

  const { currentUser, loading: authLoading } = useAuth();

  // Load MDRM reference map (mdrm -> title) from CSV once.
  useEffect(() => {
    let cancelled = false;

    const parseCsvRow = (line) => {
      // Minimal CSV parser supporting quoted fields and commas inside quotes.
      const out = [];
      let cur = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          const next = line[i + 1];
          if (inQuotes && next === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
          continue;
        }

        if (ch === ',' && !inQuotes) {
          out.push(cur);
          cur = '';
          continue;
        }

        cur += ch;
      }
      out.push(cur);
      return out.map((v) => String(v ?? '').trim());
    };

    const buildMdrmMapFromCsv = (csvText) => {
      const lines = String(csvText || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      if (lines.length < 2) return {};

      const header = parseCsvRow(lines[0]).map((h) => h.toLowerCase());
      const idxMdrm = header.indexOf('mdrm');
      const idxName = header.indexOf('mdrmname');

      if (idxMdrm < 0 || idxName < 0) return {};

      const map = {};
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvRow(lines[i]);
        const code = (cols[idxMdrm] || '').trim().toUpperCase();
        const title = (cols[idxName] || '').trim();
        if (!code || !title) continue;
        if (map[code]) continue; // keep first
        map[code] = title;
      }
      return map;
    };

    (async () => {
      try {
        const res = await fetch(mdrmRefUrl);
        const text = await res.text();
        const map = buildMdrmMapFromCsv(text);
        if (!cancelled) setMdrmTitleByCode(map);
      } catch (e) {
        console.error('[RRM] Failed to load MDRM reference CSV:', e);
        if (!cancelled) setMdrmTitleByCode({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch available folders
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const storageRef = ref(storage, 'rrm_data_files/');
        const result = await listAll(storageRef);
        const folders = result.prefixes
          .map((prefixRef) => prefixRef.fullPath.replace('rrm_data_files/', '').replace('/', ''))
          .filter(Boolean)
          .sort()
          .reverse();
        setAvailableFolders(folders);
      } catch (err) {
        console.error('[RRM] Error fetching folders from Storage:', err);
        setError('Failed to load available folders. Please refresh the page.');
        setAvailableFolders([]);
      }
    };

    if (!authLoading && currentUser) fetchFolders();
  }, [authLoading, currentUser]);

  const handleClearCurrentRun = () => {
    setTestGCSResult(null);
    setSelectedComparison(null);
    setSelectedView(null);
    setError(null);
  };

  const runTestGCSDownload = async () => {
    if (!origFolder || !newFolder) {
      setError('Please select both ORIG and NEW folders.');
      return;
    }
    if (origFolder === newFolder) {
      setError('ORIG and NEW folders must be different');
      return;
    }

    setTestGCSLoading(true);
    setTestGCSResult(null);
    try {
      const pythonFunctions = getFunctions(app, 'us-central1', 'python_functions');
      const testGCS = httpsCallable(pythonFunctions, 'testGCSDownload');
      const result = await testGCS({ oldFolder: origFolder, newFolder, maxPairs: 50 });
      setTestGCSResult(result.data);
    } catch (e) {
      console.error('Error calling testGCSDownload:', e);
      setError(e?.message || 'Failed to run comparison');
    } finally {
      setTestGCSLoading(false);
    }
  };

  const toRrmComparisonFromTest = (testComp, idx = 0) => {
    const summary = testComp?.comparison?.summary || {};
    const reportingPeriod = testComp?.code ? `BHCF${testComp.code}` : 'TEST';
    const comp = testComp?.comparison || {};

    return {
      uniqueId: `test_${reportingPeriod}_${idx}`,
      comparison_date: 'TEST',
      reporting_period: reportingPeriod,
      original_file: testComp?.old_file || '—',
      new_file: testComp?.new_file || '—',
      new_row_keys: comp.new_row_keys || [],
      deleted_row_keys: comp.deleted_row_keys || [],
      modified_row_keys: comp.modified_row_keys || [],
      new_rows_by_key: comp.new_rows_by_key || {},
      deleted_rows_by_key: comp.deleted_rows_by_key || {},
      modified_summary_by_key: comp.modified_summary_by_key || {},
      modified_field_changes_by_key: comp.modified_field_changes_by_key || {},
      summary: {
        total_yesterday: summary.total_old ?? 0,
        total_today: summary.total_new ?? 0,
        new_count: summary.new_count ?? 0,
        deleted_count: summary.deleted_count ?? 0,
        modified_count: summary.modified_count ?? 0,
        unchanged_count: summary.unchanged_count ?? 0,
        new_count_by_report: summary.new_count_by_report || null,
        deleted_count_by_report: summary.deleted_count_by_report || null,
        modified_count_by_report: summary.modified_count_by_report || null
      }
    };
  };

  const handleBoxClick = (comparison, viewType) => {
    const compId = comparison.uniqueId || comparison.id || `${comparison.comparison_date}_${comparison.reporting_period || 'default'}`;
    const selectedId = selectedComparison?.uniqueId || selectedComparison?.id;

    if (selectedId === compId && selectedView === viewType) {
      setSelectedComparison(null);
      setSelectedView(null);
    } else {
      setSelectedComparison(comparison);
      setSelectedView(viewType);
    }
  };

  const renderSummary = (comparison) => {
    const summary = comparison?.summary;
    if (!summary) return null;

    const summaryByReport = {
      new: summary?.new_count_by_report || null,
      deleted: summary?.deleted_count_by_report || null,
      modified: summary?.modified_count_by_report || null
    };

    const computeFilteredCount = (type) => {
      if (reportFilter === 'all') return null;
      const byReport = summaryByReport[type];
      if (byReport) {
        const key = reportFilter === 'unknown' ? 'unknown' : reportFilter;
        const v = byReport[key];
        return typeof v === 'number' ? v : 0;
      }
      return null;
    };

    const displaySummary = {
      ...summary,
      new_count: computeFilteredCount('new') ?? summary.new_count,
      deleted_count: computeFilteredCount('deleted') ?? summary.deleted_count,
      modified_count: computeFilteredCount('modified') ?? summary.modified_count
    };

    const compId = comparison.uniqueId || comparison.id;
    const selectedId = selectedComparison?.uniqueId || selectedComparison?.id;
    const isSelected = selectedId === compId;

    const formatReportingPeriod = (period) => {
      if (!period) return '—';
      if (period.startsWith('BHCF') && period.length === 12) {
        const year = period.substring(4, 8);
        const month = period.substring(8, 10);
        const day = period.substring(10, 12);
        return `${month}/${day}/${year}`;
      }
      return period;
    };

    return (
      <>
        <div className="rrm-summary-grid">
          <div className="rrm-summary-card rrm-new-box-left">
            <div className="rrm-summary-label">Reporting Period</div>
            <div className="rrm-summary-value">{formatReportingPeriod(comparison.reporting_period)}</div>
          </div>
          {(() => {
            const v = displaySummary.new_count || 0;
            const clickable = v > 0;
            return (
              <div
                className={`rrm-summary-card ${(v || 0) > 0 ? 'new' : ''} ${clickable ? 'clickable' : ''} ${isSelected && selectedView === 'new' ? 'selected' : ''}`}
                onClick={() => (clickable ? handleBoxClick(comparison, 'new') : null)}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (!clickable) return;
                  if (e.key === 'Enter' || e.key === ' ') handleBoxClick(comparison, 'new');
                }}
              >
                <div className="rrm-summary-label">New Reports</div>
                <div className="rrm-summary-value">{v.toLocaleString()}</div>
              </div>
            );
          })()}
          {(() => {
            const v = displaySummary.deleted_count || 0;
            const clickable = v > 0;
            return (
              <div
                className={`rrm-summary-card ${(v || 0) > 0 ? 'deleted' : ''} ${clickable ? 'clickable' : ''} ${isSelected && selectedView === 'deleted' ? 'selected' : ''}`}
                onClick={() => (clickable ? handleBoxClick(comparison, 'deleted') : null)}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (!clickable) return;
                  if (e.key === 'Enter' || e.key === ' ') handleBoxClick(comparison, 'deleted');
                }}
              >
                <div className="rrm-summary-label">Rescinded Reports</div>
                <div className="rrm-summary-value">{v.toLocaleString()}</div>
              </div>
            );
          })()}
          {(() => {
            const v = displaySummary.modified_count || 0;
            const clickable = v > 0;
            return (
              <div
                className={`rrm-summary-card ${(v || 0) > 0 ? 'modified' : ''} ${clickable ? 'clickable' : ''} ${isSelected && selectedView === 'modified' ? 'selected' : ''}`}
                onClick={() => (clickable ? handleBoxClick(comparison, 'modified') : null)}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (!clickable) return;
                  if (e.key === 'Enter' || e.key === ' ') handleBoxClick(comparison, 'modified');
                }}
              >
                <div className="rrm-summary-label">Revised Reports</div>
                <div className="rrm-summary-value">{v.toLocaleString()}</div>
              </div>
            );
          })()}
          <div className="rrm-summary-card rrm-new-box-right">
            <div className="rrm-stats-lines">
              <div className="rrm-stat-line">
                <span className="rrm-stat-label">Total Yesterday:</span>
                <span className="rrm-stat-value">{displaySummary.total_yesterday?.toLocaleString() || 0}</span>
              </div>
              <div className="rrm-stat-line">
                <span className="rrm-stat-label">Total Today:</span>
                <span className="rrm-stat-value">{displaySummary.total_today?.toLocaleString() || 0}</span>
              </div>
              <div className="rrm-stat-line">
                <span className="rrm-stat-label">Unchanged Rows:</span>
                <span className="rrm-stat-value">{displaySummary.unchanged_count?.toLocaleString() || 0}</span>
              </div>
            </div>
          </div>
          <div className="rrm-summary-card rrm-files-box">
            <div className="rrm-files-lines">
              <div className="rrm-file-line">
                <span className="rrm-file-label">Original</span>
                <span className="rrm-file-value">{comparison.original_file || '—'}</span>
              </div>
              <div className="rrm-file-line">
                <span className="rrm-file-label">New</span>
                <span className="rrm-file-value">{comparison.new_file || '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderDetails = (comparison) => {
    const compId = comparison.uniqueId || comparison.id;
    const selectedId = selectedComparison?.uniqueId || selectedComparison?.id;
    if (!selectedView || selectedId !== compId) return null;

    const viewLabel =
      selectedView === 'new'
        ? 'New Reports'
        : selectedView === 'deleted'
          ? 'Rescinded Reports'
          : 'Revised Reports';

    const close = () => {
      setSelectedComparison(null);
      setSelectedView(null);
    };

    // For "modified" we render a formatted MDRM change table (no RSSD dropdown).
    if (selectedView === 'modified') {
      const toNum = (v) => {
        if (v === null || v === undefined) return null;
        const n = Number(String(v).replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : null;
      };

      const pickAssets = (summary) => {
        const candidates = [
          { field: 'BHCK2170', report: 'FR Y-9C' },
          { field: 'BHCP2170', report: 'FR Y-9LP' },
          { field: 'BHSP2170', report: 'FR Y-9SP' }
        ];
        let best = { assets: null, report: 'unknown' };
        for (const c of candidates) {
          const n = toNum(summary?.[c.field]);
          if (n === null || n <= 0) continue;
          if (best.assets === null || n > best.assets) best = { assets: n, report: c.report };
        }
        return best;
      };

      const byKeySummary = comparison.modified_summary_by_key || {};
      const byKeyChanges = comparison.modified_field_changes_by_key || {};

      const normalizeRowKey = (k) => {
        if (k === null || k === undefined) return null;
        if (typeof k === 'string' || typeof k === 'number') return String(k);
        if (typeof k === 'object') {
          if (k.key !== undefined && k.key !== null) return String(k.key);
          if (k.row_key !== undefined && k.row_key !== null) return String(k.row_key);
          if (k.id !== undefined && k.id !== null) return String(k.id);
        }
        return null;
      };

      // Prefer the map keys (authoritative) and union with any provided modified_row_keys list.
      const arrayKeys = (Array.isArray(comparison.modified_row_keys) ? comparison.modified_row_keys : [])
        .map(normalizeRowKey)
        .filter(Boolean);
      const summaryKeys = Object.keys(byKeySummary || {});
      const changeKeys = Object.keys(byKeyChanges || {});
      const allKeys = Array.from(new Set([...summaryKeys, ...changeKeys, ...arrayKeys]));

      const filterKey = reportFilter === 'unknown' ? 'unknown' : reportFilter;
      const filteredKeys =
        reportFilter === 'all'
          ? allKeys
          : allKeys.filter((k) => {
              const summary = byKeySummary?.[k] || {};
              const { report } = pickAssets(summary);
              const normalized = report === 'unknown' ? 'unknown' : report;
              return normalized === filterKey;
            });

      const total = filteredKeys.length;
      const limit = 200;
      const shownKeys = filteredKeys.slice(0, limit);

      return (
        <div className="rrm-details">
          <div className="rrm-details-header">
            <div className="rrm-details-title">
              {viewLabel} details
              <span className="rrm-details-count">{total.toLocaleString()}</span>
              {total > limit ? <span className="rrm-details-note">Showing first {limit}</span> : null}
            </div>
            <div className="rrm-details-actions">
              <button type="button" className="rrm-details-btn rrm-details-btn-close" onClick={close}>
                Close
              </button>
            </div>
          </div>

          <div className="rrm-details-formatted">
            {shownKeys.length === 0 ? (
              <div className="rrm-details-empty">No revised fields found for the selected filter.</div>
            ) : (
              <div className="rrm-details-records">
                {shownKeys.map((key, keyIdx) => {
                const summary = byKeySummary?.[key] || {};
                const changes = byKeyChanges?.[key] || [];
                const { assets, report } = pickAssets(summary);
                const rssd9017 = summary?.RSSD9017 ?? '—';

                return (
                  <div key={`${key}_${keyIdx}`} className="rrm-details-record">
                    <div className="rrm-details-line1">
                      <div className="rrm-details-kv">
                        <span className="rrm-details-k">RSSD9017</span>
                        <span className="rrm-details-v">{String(rssd9017)}</span>
                      </div>
                      <div className="rrm-details-kv">
                        <span className="rrm-details-k">Total Assets (000s)</span>
                        <span className="rrm-details-v">{assets === null ? '—' : assets.toLocaleString()}</span>
                      </div>
                      <div className="rrm-details-kv">
                        <span className="rrm-details-k">Report</span>
                        <span className="rrm-details-v">{report}</span>
                      </div>
                    </div>

                    <div className="rrm-details-line2">
                      <div className="rrm-details-table">
                        <div className="rrm-details-tr rrm-details-th">
                          <div className="rrm-details-td">MDRM</div>
                          <div className="rrm-details-td">Title</div>
                          <div className="rrm-details-td">New Value</div>
                          <div className="rrm-details-td">Old Value</div>
                          <div className="rrm-details-td">Diff</div>
                        </div>
                        {Array.isArray(changes) && changes.length ? (
                          changes.map((c, idx) => {
                            const mdrm = c?.mdrm ?? c?.MDRM ?? c?.field ?? c?.code ?? '—';
                            const mdrmKey = String(mdrm).trim().toUpperCase();
                            const mdrmTitleRaw = mdrmTitleByCode?.[mdrmKey] || '—';
                            const mdrmTitle =
                              typeof mdrmTitleRaw === 'string' && mdrmTitleRaw.length > 200
                                ? `${mdrmTitleRaw.slice(0, 199)}…`
                                : mdrmTitleRaw;
                            return (
                              <div key={`${key}_${String(mdrm)}_${idx}`} className="rrm-details-tr">
                                <div className="rrm-details-td rrm-details-mdrm">{String(mdrm)}</div>
                                <div className="rrm-details-td rrm-details-mdrm-title">{mdrmTitle}</div>
                                <div className="rrm-details-td">{c?.new ?? '—'}</div>
                                <div className="rrm-details-td">{c?.old ?? '—'}</div>
                                <div className="rrm-details-td">{c?.diff ?? '—'}</div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="rrm-details-empty">No revised fields found for this record.</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        </div>
      );
    }

    // For new/deleted we keep a simple key list (not requested to change yet).
    const byKey = selectedView === 'new' ? comparison.new_rows_by_key || {} : comparison.deleted_rows_by_key || {};
    const keys = selectedView === 'new' ? comparison.new_row_keys || Object.keys(byKey) : comparison.deleted_row_keys || Object.keys(byKey);
    const total = Array.isArray(keys) ? keys.length : 0;
    const limit = 200;
    const shownKeys = (Array.isArray(keys) ? keys : []).slice(0, limit);

    const toNum = (v) => {
      if (v === null || v === undefined) return null;
      const n = Number(String(v).replace(/,/g, '').trim());
      return Number.isFinite(n) ? n : null;
    };

    const pickAssetsAndReport = (row) => {
      const candidates = [
        { field: 'BHCK2170', report: 'FR Y-9C' },
        { field: 'BHCP2170', report: 'FR Y-9LP' },
        { field: 'BHSP2170', report: 'FR Y-9SP' }
      ];
      let best = { assets: null, report: 'unknown' };
      for (const c of candidates) {
        const n = toNum(row?.[c.field]);
        if (n === null || n <= 0) continue;
        if (best.assets === null || n > best.assets) best = { assets: n, report: c.report };
      }
      return best;
    };

    return (
      <div className="rrm-details">
        <div className="rrm-details-header">
          <div className="rrm-details-title">
            {viewLabel} details
            <span className="rrm-details-count">{total.toLocaleString()}</span>
            {total > limit ? <span className="rrm-details-note">Showing first {limit}</span> : null}
          </div>
          <div className="rrm-details-actions">
            <button type="button" className="rrm-details-btn rrm-details-btn-close" onClick={close}>
              Close
            </button>
          </div>
        </div>

        <div className="rrm-details-formatted">
          {shownKeys.length === 0 ? (
            <div className="rrm-details-empty">No rows to display for this category.</div>
          ) : (
            <div className="rrm-details-table">
              <div className="rrm-details-tr rrm-details-tr3 rrm-details-th">
                <div className="rrm-details-td">RSSD9017</div>
                <div className="rrm-details-td">Total Assets (000s)</div>
                <div className="rrm-details-td">Report</div>
              </div>
              {shownKeys.map((k) => {
                const row = byKey?.[k] || {};
                const rssd9017 = row?.RSSD9017 ?? '—';
                const { assets, report } = pickAssetsAndReport(row);
                return (
                  <div key={k} className="rrm-details-tr rrm-details-tr3">
                    <div className="rrm-details-td">{String(rssd9017)}</div>
                    <div className="rrm-details-td">{assets === null ? '—' : assets.toLocaleString()}</div>
                    <div className="rrm-details-td">{report}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleExportToExcel = () => {
    if (!origFolder || !newFolder) return;
    const pythonFunctions = getFunctions(app, 'us-central1', 'python_functions');
    const testGCS = httpsCallable(pythonFunctions, 'testGCSDownload');

    (async () => {
      const exportResult = await testGCS({
        oldFolder: origFolder,
        newFolder,
        maxPairs: 50,
        newLimit: 100,
        deletedLimit: 100,
        modifiedLimit: 0
      });
      const comps = exportResult?.data?.results?.comparisons || [];
      if (!Array.isArray(comps) || comps.length === 0) return;

      const extractPeriodKey = (comp) => (comp?.code ? String(comp.code) : '');
      const sortLatestFirst = sortOrder === 'latest';
      const sorted = [...comps].sort((a, b) => (sortLatestFirst ? extractPeriodKey(b).localeCompare(extractPeriodKey(a)) : extractPeriodKey(a).localeCompare(extractPeriodKey(b))));

      const wb = XLSX.utils.book_new();
      const summaryRows = sorted.map((comp, idx) => {
        const mapped = toRrmComparisonFromTest(comp, idx);
        const s = mapped.summary || {};
        const pickByReport = (byReport) => {
          if (!byReport || reportFilter === 'all') return null;
          const key = reportFilter === 'unknown' ? 'unknown' : reportFilter;
          return typeof byReport[key] === 'number' ? byReport[key] : 0;
        };
        return {
          ReportingPeriod: mapped.reporting_period || '',
          Filter: reportFilter,
          NewReports: pickByReport(s.new_count_by_report) ?? s.new_count ?? 0,
          RescindedReports: pickByReport(s.deleted_count_by_report) ?? s.deleted_count ?? 0,
          RevisedReports: pickByReport(s.modified_count_by_report) ?? s.modified_count ?? 0,
          OriginalFile: mapped.original_file || '',
          NewFile: mapped.new_file || ''
        };
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Summary');

      XLSX.writeFile(wb, `RRMonitor_${newFolder}_vs_${origFolder}_${reportFilter}.xlsx`);
    })().catch((e) => {
      console.error('Export failed:', e);
      setError(`Export failed: ${e?.message || 'Unknown error'}`);
    });
  };

  if (authLoading) {
    return (
      <Shell>
        <div className="rrm-page">
          <div className="rrm-container">
            <p className="loading">Loading...</p>
          </div>
        </div>
      </Shell>
    );
  }

  const hasResults = (testGCSResult?.results?.comparisons?.length || 0) > 0;

  const renderReportFilter = () => (
    <div className="rrm-results-toolbar">
      <div className="rrm-results-toolbar-spacer" />
      <div className="rrm-report-filter-buttons" role="group" aria-label="Report filter">
        <button type="button" className={`rrm-filter-button ${reportFilter === 'all' ? 'active' : ''}`} onClick={() => setReportFilter('all')}>All</button>
        <span className="rrm-filter-divider" aria-hidden="true" />
        {[
          { value: 'FR Y-9C', label: 'FR Y-9C' },
          { value: 'FR Y-9LP', label: 'FR Y-9LP' },
          { value: 'FR Y-9SP', label: 'FR Y-9SP' },
          { value: 'unknown', label: 'Misc' }
        ].map((opt) => (
          <button key={opt.value} type="button" className={`rrm-filter-button ${reportFilter === opt.value ? 'active' : ''}`} onClick={() => setReportFilter(opt.value)}>
            {opt.label}
          </button>
        ))}
      </div>
      <button type="button" className="rrm-export-link" onClick={handleExportToExcel}>Export to Excel</button>
    </div>
  );

  return (
    <Shell>
      <div className="rrm-page">
        <div className="rrm-container">
          <div className="rrm-header">
            <h1>RRM Data Comparison</h1>
            <p>Automated comparison of daily data files</p>

            <div className="rrm-folder-selector">
              <div className="rrm-folder-select-group">
                <label htmlFor="new-folder" className="rrm-folder-label">NEW Folder:</label>
                <select id="new-folder" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} className="rrm-folder-select" disabled={testGCSLoading}>
                  <option value="">Select NEW folder...</option>
                  {availableFolders.map((folder) => <option key={folder} value={folder}>{folder}</option>)}
                </select>
              </div>

              <div className="rrm-folder-select-group">
                <label htmlFor="orig-folder" className="rrm-folder-label">ORIG Folder:</label>
                <select id="orig-folder" value={origFolder} onChange={(e) => setOrigFolder(e.target.value)} className="rrm-folder-select" disabled={testGCSLoading}>
                  <option value="">Select ORIG folder...</option>
                  {availableFolders.map((folder) => <option key={folder} value={folder}>{folder}</option>)}
                </select>
              </div>

              <div className="rrm-folder-select-group">
                <label htmlFor="rrm-sort-order" className="rrm-folder-label">Sort:</label>
                <select id="rrm-sort-order" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="rrm-folder-select" disabled={testGCSLoading}>
                  <option value="latest">Latest → Oldest</option>
                  <option value="oldest">Oldest → Latest</option>
                </select>
              </div>

              <button className="rrm-run-button" type="button" disabled={testGCSLoading || !origFolder || !newFolder} onClick={runTestGCSDownload}>
                {testGCSLoading ? 'Running...' : 'Run'}
              </button>

              <button className="rrm-clear-button" type="button" disabled={testGCSLoading} onClick={handleClearCurrentRun}>
                Clear Current Run
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="rrm-content">
            {hasResults ? (
              <>
                {renderReportFilter()}
                <div className="rrm-latest-comparison">
                  {[...testGCSResult.results.comparisons]
                    .sort((a, b) => (sortOrder === 'latest' ? String(b.code).localeCompare(String(a.code)) : String(a.code).localeCompare(String(b.code))))
                    .map((comp, idx) => {
                      const mapped = toRrmComparisonFromTest(comp, idx);
                      return (
                        <div key={mapped.uniqueId || idx} className="rrm-period-section">
                          {renderSummary(mapped)}
                          {renderDetails(mapped)}
                        </div>
                      );
                    })}
                </div>
              </>
            ) : (
              <div className="no-data-message">
                <p>Results will appear here.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default RRM;


